// AdminSlatesPage.tsx — (TF-6b-3; design §3) Admin → Slates: the list. From the engine's list once it has one; until then
// (LIST_NOT_AVAILABLE) the slates opened in THIS browser, each read from the engine, and the page says why.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { engineSlatesApi } from "@/api/engine-slates.ts";
import { seriesHistory } from "@/lib/series.ts";
import { slateRefusalText, SLATE_HISTORY_KEY } from "@/lib/slates.ts";

type Row = { id: string; when?: string; status?: string; kept?: number; total?: number; err?: string };
const storage = () => { try { return window.localStorage; } catch { return null; } };   // catch-reason: no storage → no history

export function AdminSlatesPage() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [fallback, setFallback] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    engineSlatesApi.list().then((r) => setRows(r.slates.map((s) => ({ id: s.id, when: s.createdAt, status: s.status, kept: s.ideasKept, total: s.ideasTotal }))))
      .catch(async (e) => {   // catch-reason: LIST_NOT_AVAILABLE → this browser's history; anything else is shown
        if ((e as { code?: string }).code !== "LIST_NOT_AVAILABLE") { setErr(slateRefusalText(e)); return; }
        setFallback(true);
        setRows(await Promise.all(seriesHistory(storage(), SLATE_HISTORY_KEY).map(async (h): Promise<Row> => {
          try {
            const s = await engineSlatesApi.get(h.id);
            return { id: h.id, when: h.at, status: s.status, kept: s.cards.filter((c) => c.status === "kept" || c.status === "edited").length, total: s.cards.length };
          } catch (x) { return { id: h.id, when: h.at, err: slateRefusalText(x) }; }   // catch-reason: the row says it couldn't be read
        })));
      });
  }, []);
  return (
    <PageContainer title="Slates" subtitle="Idea slates on tubeforge:growth: keep, edit or kill each card, then pilot the kept ones."
      action={<Link to="/admin/slates/new"><Btn>New slate</Btn></Link>}>
      {fallback && <TfCard><div style={{ fontSize: 13 }}>Showing the slates opened in this browser. The engine can't list slates yet.</div></TfCard>}
      {err && <div style={{ color: "#b91c1c", fontSize: 13, margin: "8px 0" }}>The slates couldn't be read: {err}</div>}
      {rows === null && !err && <div className="tf-muted">Reading…</div>}
      {rows && rows.length === 0 && <div className="tf-muted" style={{ marginTop: 12 }}>{fallback ? "No slates opened in this browser yet." : "No slates yet."}</div>}
      {rows && rows.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12, fontSize: 14 }}>
          <thead><tr style={{ textAlign: "left" }}><th>Slate</th><th>Made</th><th>Status</th><th>Kept / ideas</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid var(--tf-border, #e5e7eb)" }}>
              <td><Link to={`/admin/slates/${r.id}`}>{r.id.slice(0, 8)}</Link></td>
              <td>{r.when ? new Date(r.when).toLocaleString() : "—"}</td>
              {r.err ? <td colSpan={2} style={{ color: "#b91c1c" }}>couldn't be read: {r.err}</td> : <><td>{r.status}</td><td>{r.kept ?? "—"} / {r.total ?? "—"}</td></>}
            </tr>))}</tbody>
        </table>)}
    </PageContainer>
  );
}
